// The breakdown's submission info (GET breakdowns/{bd}/submission-info), kept for a minute per
// breakdown and role in this tab (decided 2026-10-29). Every step asks for it on the way in (the
// gate re-check), so a submission asked the same question six times; now once a minute, as long as
// the server keeps its own answer (the shared short cache tier). The server still checks the
// breakdown on every save and on Send, so nothing kept here can let a closed one through.
//
// A kept answer is handed out as it would read now: its server clock moved on by the time it was
// kept, so the deadline check and the countdown stay exact. An open breakdown within two minutes
// of its deadline isn't kept at all, nor a failure. A server answer that a breakdown closed, or a
// role was cast, forgets everything kept (forgetBreakdownInfo).

import { getMockScenario, getSubmissionFlowApi, isMockApi } from "../../../shared/api/submission";
import { IBreakdownSubmissionInfoDto } from "../../../shared/api/submission/types";

export const INFO_MAX_AGE_MS = 60 * 1000;

interface Kept {
  at: number;
  promise: Promise<IBreakdownSubmissionInfoDto>;
}

const kept: { [key: string]: Kept } = {};

// In the mock, the scenario too: switching to "expired" in the same tab must show it at once.
const keyOf = (breakdownId: string, roleId?: string | null) =>
  `${breakdownId}:${roleId || ""}${isMockApi() ? `:${getMockScenario()}` : ""}`;

const aged = (info: IBreakdownSubmissionInfoDto, ms: number): IBreakdownSubmissionInfoDto => {
  const server = Date.parse(info.serverTimeUtc);
  return isNaN(server) || ms <= 0 ? info : { ...info, serverTimeUtc: new Date(server + ms).toISOString() };
};

const keepable = (info: IBreakdownSubmissionInfoDto): boolean => {
  if (!info || info.status !== "Open" || info.isLate) return !!info;
  const left = Date.parse(info.closingDateUtc) - Date.parse(info.serverTimeUtc);
  return isNaN(left) || left > 2 * INFO_MAX_AGE_MS;
};

/** The info for a breakdown (and role): the one kept in the last minute, else the server's. */
export const getBreakdownInfo = (
  breakdownId: string,
  roleId?: string | null
): Promise<IBreakdownSubmissionInfoDto> => {
  const key = keyOf(breakdownId, roleId);
  const hit = kept[key];
  if (hit && Date.now() - hit.at <= INFO_MAX_AGE_MS) {
    return hit.promise.then((info) => aged(info, Date.now() - hit.at));
  }
  const entry: Kept = {
    at: Date.now(),
    promise: getSubmissionFlowApi().getBreakdownSubmissionInfo(breakdownId, roleId || undefined),
  };
  kept[key] = entry;
  entry.promise.then(
    (info) => {
      // Aged from when the answer came, not when it was asked.
      entry.at = Date.now();
      if (!keepable(info) && kept[key] === entry) delete kept[key];
    },
    () => {
      if (kept[key] === entry) delete kept[key];
    }
  );
  return entry.promise;
};

/** Everything kept, gone: the server said a breakdown closed or a role was cast. */
export const forgetBreakdownInfo = () => {
  Object.keys(kept).forEach((key) => delete kept[key]);
};

const CLOSING_CODES = ["breakdownClosed", "roleCast", "breakdownNotFound", "roleNotFound", "roleRequired"];

/** For the models' error handling: an answer that means the kept info is out of date. */
export const forgetBreakdownInfoOn = (code: string | null | undefined) => {
  if (code && CLOSING_CODES.indexOf(code) > -1) forgetBreakdownInfo();
};
